/**
 * L1J Web Client - Bookmark Dialog
 *
 * 순간이동 주문서(use_type 6 ntele / 29 btele)의 **목적지 선택 창** (화면 중앙, 기본 숨김).
 *
 * 서버 `C_UseItem`은 순간이동 계열 아이템에서 itemObjId 뒤로 `readH()`(mapId) +
 * `readD()`(북마크 objId)를 더 읽는다. 즉 목적지를 정하는 주체는 클라이언트이고,
 * 그 후보는 서버가 인게임 진입 시 보내는 `S_BookmarkLoad`가 전부다:
 *
 *   writeH(locX) + writeH(locY) + writeS(name) + writeH(mapId) + writeD(objId)
 *
 * 이 창은 그 목록을 그대로 보여주고 고른 하나를 호출부로 돌려줄 뿐이다 —
 * "무슨 주문서를 쓰는 중인지"는 알지 못한다. 그 상태는 GameScene이 갖는다.
 *
 * 구조·스타일은 HtmlDialog / Inventory와 같은 규약을 따른다:
 *   - Phaser Container + setScrollFactor(0) + UI 카메라 전용
 *   - 자식에게도 setScrollFactor(0)을 직접 걸어야 클릭 히트테스트가 맞는다
 *   - 목록은 Inventory와 같은 가상 스크롤 (행 오브젝트는 고정, 표시 구간만 옮긴다)
 *   - 닫기는 X 버튼 / [닫기] 버튼 / ESC(GameScene이 hide()를 부른다) 세 경로
 */

import Phaser from 'phaser';
import { BookmarkEntry } from '../../types/network';

/** 목록 한 줄의 높이(px). */
const ROW_HEIGHT = 32;

/** 한 화면에 보여줄 줄 수. 넘는 만큼은 휠/스크롤바로 넘긴다. */
const VISIBLE_ROWS = 7;

/** 줄 배경 (평상시 · 마우스 오버). */
const ROW_BG_COLOR = 0x2a2a3a;
const ROW_BG_HOVER_COLOR = 0x3a3a5a;
const ROW_BORDER_COLOR = 0x3a3a5a;
const ROW_BORDER_HOVER_COLOR = 0xffd700;

export class BookmarkDialog extends Phaser.GameObjects.Container {
  private panelWidth = 320;
  private panelHeight = 320;

  /** 목록 영역 좌측 X / 상단 Y / 폭 / 높이 (모두 컨테이너 로컬 좌표). */
  private listLeft = -this.panelWidth / 2 + 12;
  private listTop = -this.panelHeight / 2 + 40;
  private listWidth = this.panelWidth - 24 - 14; // 오른쪽 14px는 스크롤바 자리
  private listHeight = VISIBLE_ROWS * ROW_HEIGHT;

  private bookmarks: BookmarkEntry[] = [];
  private rows: BookmarkRow[] = [];

  private countText!: Phaser.GameObjects.Text;
  private emptyText!: Phaser.GameObjects.Text;

  private scrollTrack!: Phaser.GameObjects.Rectangle;
  private scrollThumb!: Phaser.GameObjects.Rectangle;
  /** 트랙을 누른 채 끄는 중인지. 트랙 밖으로 나가도 이어져야 해서 씬 단위로 추적한다. */
  private isScrubbing = false;

  /**
   * 스크롤 위치(줄 단위). 줄 오브젝트는 VISIBLE_ROWS개로 고정하고 표시 구간만
   * 옮기는 가상 스크롤이다 (Inventory와 같은 방식).
   */
  private scrollRow = 0;

  /** show()에서 받은 콜백. 결과를 한 번만 돌려주기 위해 호출 직후 비운다. */
  private pickCallback: ((bookmark: BookmarkEntry) => void) | null = null;
  private cancelCallback: (() => void) | null = null;

  /** 씬 input에 직접 건 리스너. destroy 때 해제해야 유령 리스너가 남지 않는다. */
  private sceneInput: Phaser.Input.InputPlugin;
  private onWheel!: (pointer: Phaser.Input.Pointer, objs: unknown, dx: number, dy: number) => void;
  private onPointerMove!: (pointer: Phaser.Input.Pointer) => void;
  private onPointerUp!: () => void;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.sceneInput = scene.input;
    scene.add.existing(this);

    this.setScrollFactor(0);

    this.createUI();
    this.registerScrollInput();
    // 인벤토리(200)에서 주문서를 더블클릭해 열리므로 그보다 위여야 한다.
    // ESC 메뉴/대상 선택 배너(300)보다는 아래.
    this.setDepth(210);
    this.setVisible(false);
  }

  public getPanelWidth(): number {
    return this.panelWidth;
  }

  public getPanelHeight(): number {
    return this.panelHeight;
  }

  /** 창이 열려 있는지. ESC 처리 우선순위 판단에 쓴다. */
  public isOpen(): boolean {
    return this.visible;
  }

  private createUI(): void {
    const w = this.panelWidth;
    const h = this.panelHeight;

    const bg = this.scene.add.rectangle(0, 0, w, h, 0x1a1a2e, 0.96);
    bg.setStrokeStyle(2, 0x4a4a6a);
    this.add(bg);

    const titleBar = this.scene.add.rectangle(0, -h / 2 + 15, w, 30, 0x2a2a4e);
    titleBar.setStrokeStyle(1, 0x3a3a5a);
    this.add(titleBar);

    const titleText = this.scene.add.text(-w / 2 + 12, -h / 2 + 15, '순간이동 목적지', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    titleText.setOrigin(0, 0.5);
    this.add(titleText);

    const closeBtn = this.scene.add.text(w / 2 - 20, -h / 2 + 15, 'X', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ff6666',
    });
    closeBtn.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    // 걸지 않으면 카메라가 스크롤된 상태에서 히트테스트가 월드 좌표로 계산돼 빗나간다.
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setScrollFactor(0);
    closeBtn.on('pointerover', () => closeBtn.setStyle({ color: '#ffaaaa' }));
    closeBtn.on('pointerout', () => closeBtn.setStyle({ color: '#ff6666' }));
    // 닫은 뒤 씬이 이 클릭을 월드 클릭으로 오해하지 않도록 알린다
    // (Phaser는 오브젝트 핸들러를 먼저 돌리고 씬 pointerdown을 나중에 쏜다).
    // hide() 자체에는 넣지 않는다 — ESC/코드로 닫힐 때는 클릭이 없다.
    closeBtn.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.add(closeBtn);

    this.createRows();
    this.createScrollBar();

    // 북마크가 하나도 없을 때만 보이는 안내.
    this.emptyText = this.scene.add.text(0, this.listTop + this.listHeight / 2, '등록된 북마크가 없습니다', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.emptyText.setOrigin(0.5);
    this.add(this.emptyText);

    this.countText = this.scene.add.text(0, h / 2 - 46, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.countText.setOrigin(0.5);
    this.add(this.countText);

    // 하단 닫기 버튼 — X는 작아서 놓치기 쉬우므로 목록 아래에도 둔다.
    const closeLabel = this.scene.add.text(0, h / 2 - 22, '[ 닫기 (ESC) ]', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    closeLabel.setOrigin(0.5);
    closeLabel.setInteractive({ useHandCursor: true });
    closeLabel.setScrollFactor(0);
    closeLabel.on('pointerover', () => closeLabel.setStyle({ color: '#ffd700' }));
    closeLabel.on('pointerout', () => closeLabel.setStyle({ color: '#ffffff' }));
    closeLabel.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.add(closeLabel);
  }

  /** 줄 오브젝트를 미리 만들어 두고 내용만 갈아 끼운다 (Inventory 슬롯과 같은 방식). */
  private createRows(): void {
    for (let i = 0; i < VISIBLE_ROWS; i++) {
      const row = new BookmarkRow(
        this.scene,
        this.listLeft + this.listWidth / 2,
        this.listTop + ROW_HEIGHT / 2 + i * ROW_HEIGHT,
        this.listWidth,
        ROW_HEIGHT - 2,
        (bookmark) => this.pick(bookmark)
      );
      this.rows.push(row);
      this.add(row);
    }
  }

  /**
   * 스크롤바. 목록이 한 화면에 다 들어가면 숨는다.
   *
   * 썸은 순수 시각 요소이고 입력은 트랙이 받는다 — 썸은 내용량에 따라 높이가 바뀌는데
   * Phaser에서 setSize()는 이미 등록된 hitArea를 갱신하지 않아 클릭 판정이 어긋난다.
   */
  private createScrollBar(): void {
    const trackX = this.panelWidth / 2 - 14;
    const trackY = this.listTop + this.listHeight / 2;
    const trackWidth = 6;

    this.scrollTrack = this.scene.add.rectangle(trackX, trackY, trackWidth, this.listHeight, 0x0a0a15);
    this.scrollTrack.setStrokeStyle(1, 0x2a2a4a);
    this.scrollTrack.setScrollFactor(0);
    // 6px 트랙은 그대로 두면 집기 어려우므로 판정 영역만 좌우로 넓힌다.
    // hitArea 좌표는 오브젝트 원점이 아니라 좌상단 기준(0,0)~(width,height)이다.
    this.scrollTrack.setInteractive(
      new Phaser.Geom.Rectangle(-(24 - trackWidth) / 2, 0, 24, this.listHeight),
      Phaser.Geom.Rectangle.Contains
    );
    this.scrollTrack.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isScrubbing = true;
      this.scrubToPointer(pointer);
    });
    this.add(this.scrollTrack);

    this.scrollThumb = this.scene.add.rectangle(trackX, trackY, trackWidth, 40, 0x4a4a6a);
    this.scrollThumb.setScrollFactor(0);
    this.add(this.scrollThumb);

    this.updateScrollBar();
  }

  /**
   * 휠 스크롤과 스크롤바 드래그를 씬 input에 등록한다.
   *
   * 줄 배경이 패널 배경을 덮고 있어 오브젝트 단위 wheel 이벤트로는 줄 위에서 굴린 휠을
   * 놓친다. 그래서 씬 전역에서 받고 포인터가 패널 안인지 직접 판정한다 (Inventory와 동일).
   */
  private registerScrollInput(): void {
    this.onWheel = (pointer, _objs, _dx, dy) => {
      if (!this.visible || !this.isPointerOverPanel(pointer)) return;
      this.setScrollRow(this.scrollRow + (dy > 0 ? 1 : -1));
    };

    // 트랙 밖으로 포인터가 나가도 드래그가 이어져야 하므로 씬 단위로 받는다.
    this.onPointerMove = (pointer) => {
      if (this.isScrubbing) this.scrubToPointer(pointer);
    };
    this.onPointerUp = () => {
      this.isScrubbing = false;
    };

    this.sceneInput.on('wheel', this.onWheel);
    this.sceneInput.on('pointermove', this.onPointerMove);
    this.sceneInput.on('pointerup', this.onPointerUp);

    // 씬이 재시작돼도 유령 리스너가 남지 않도록 파괴 시 해제한다.
    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.sceneInput.off('wheel', this.onWheel);
      this.sceneInput.off('pointermove', this.onPointerMove);
      this.sceneInput.off('pointerup', this.onPointerUp);
    });
  }

  /** 포인터가 패널 사각형 안에 있는지. 패널은 scrollFactor 0이라 화면 좌표로 바로 비교한다. */
  private isPointerOverPanel(pointer: Phaser.Input.Pointer): boolean {
    const dx = Math.abs(pointer.x - this.x);
    const dy = Math.abs(pointer.y - this.y);
    return dx <= this.panelWidth / 2 && dy <= this.panelHeight / 2;
  }

  /** 스크롤 가능한 최대 줄. 0이면 스크롤할 내용이 없다. */
  private getMaxScrollRow(): number {
    return Math.max(0, this.bookmarks.length - VISIBLE_ROWS);
  }

  private setScrollRow(row: number): void {
    const clamped = Phaser.Math.Clamp(row, 0, this.getMaxScrollRow());
    if (clamped === this.scrollRow) return;
    this.scrollRow = clamped;
    this.renderRows();
  }

  /** 트랙에서의 포인터 Y를 스크롤 줄로 환산한다. */
  private scrubToPointer(pointer: Phaser.Input.Pointer): void {
    const maxScroll = this.getMaxScrollRow();
    if (maxScroll === 0) return;

    // 컨테이너 로컬 좌표로 변환 후 트랙 내 비율(0~1)로 환산
    const localY = pointer.y - this.y;
    const ratio = Phaser.Math.Clamp((localY - this.listTop) / this.listHeight, 0, 1);
    this.setScrollRow(Math.round(ratio * maxScroll));
  }

  /** 스크롤 위치·내용량에 맞춰 썸의 크기와 위치를 다시 계산한다. */
  private updateScrollBar(): void {
    const maxScroll = this.getMaxScrollRow();
    const needed = maxScroll > 0;
    this.scrollTrack.setVisible(needed);
    this.scrollThumb.setVisible(needed);
    if (!needed) return;

    const thumbHeight = Math.max(20, (this.listHeight * VISIBLE_ROWS) / this.bookmarks.length);
    const travel = this.listHeight - thumbHeight;

    this.scrollThumb.setSize(this.scrollThumb.width, thumbHeight);
    this.scrollThumb.y = this.listTop + thumbHeight / 2 + (this.scrollRow / maxScroll) * travel;
  }

  /** 현재 스크롤 위치에 해당하는 구간만 줄에 그린다. */
  private renderRows(): void {
    this.rows.forEach((row, i) => {
      const bookmark = this.bookmarks[this.scrollRow + i];
      if (bookmark) {
        row.setBookmark(bookmark);
      } else {
        row.clear();
      }
    });

    this.updateScrollBar();
  }

  /**
   * 북마크 목록을 채우고 창을 연다.
   *
   * - `onPick`은 줄을 클릭했을 때 한 번만 불린다. 호출 직후 창은 스스로 닫힌다.
   * - `onCancel`은 X / [닫기] / ESC(= 씬이 hide()를 부른 경우) 어느 경로로 닫혀도 불린다.
   * - 두 콜백은 서로 배타적이다 — 고른 뒤에 onCancel이 따라오지 않는다.
   */
  public show(
    bookmarks: BookmarkEntry[],
    onPick: (bookmark: BookmarkEntry) => void,
    onCancel: () => void
  ): void {
    this.bookmarks = [...bookmarks];
    this.pickCallback = onPick;
    this.cancelCallback = onCancel;

    this.scrollRow = 0;

    const empty = this.bookmarks.length === 0;
    this.emptyText.setVisible(empty);
    this.countText.setText(empty ? '' : `총 ${this.bookmarks.length}개 — 목적지를 선택하세요`);

    this.renderRows();
    this.setVisible(true);
  }

  /**
   * 창을 닫는다. 닫혀 있으면 아무 일도 하지 않는다.
   *
   * 여기서 onCancel이 불린다 — ESC로 닫을 때 씬이 이 함수만 부르면 되도록.
   * `panelclose`는 여기서 쏘지 않는다 (클릭이 없는 경로에서는 플래그만 남는다).
   */
  public hide(): void {
    if (!this.visible) return;
    this.setVisible(false);

    // 콜백을 먼저 떼어내 재진입 시 두 번 불리지 않게 한다.
    const cancel = this.cancelCallback;
    this.pickCallback = null;
    this.cancelCallback = null;
    cancel?.();
  }

  /** 줄 클릭 → 선택 확정. onCancel은 부르지 않는다. */
  private pick(bookmark: BookmarkEntry): void {
    const callback = this.pickCallback;
    this.pickCallback = null;
    this.cancelCallback = null;
    this.setVisible(false);

    // 선택 클릭도 창을 닫는 클릭이므로 월드로 새지 않게 알린다 (닫기 버튼과 같은 이유).
    this.emit('panelclose');
    callback?.(bookmark);
  }
}

/**
 * 북마크 목록의 한 줄.
 *
 * 이름 + (맵ID, x, y)를 보여주고 클릭하면 선택을 알린다.
 * 스크롤로 내용이 갈리므로 줄 자체는 상태를 갖지 않는다.
 */
class BookmarkRow extends Phaser.GameObjects.Container {
  private bg!: Phaser.GameObjects.Rectangle;
  private nameText!: Phaser.GameObjects.Text;
  private locText!: Phaser.GameObjects.Text;
  private bookmark: BookmarkEntry | null = null;

  private onPick: (bookmark: BookmarkEntry) => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    onPick: (bookmark: BookmarkEntry) => void
  ) {
    super(scene, x, y);
    this.onPick = onPick;

    this.createUI(width, height);
    scene.add.existing(this);
  }

  private createUI(width: number, height: number): void {
    this.bg = this.scene.add.rectangle(0, 0, width, height, ROW_BG_COLOR);
    this.bg.setStrokeStyle(1, ROW_BORDER_COLOR);
    this.add(this.bg);

    this.nameText = this.scene.add.text(-width / 2 + 8, -1, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    this.nameText.setOrigin(0, 0.5);
    this.add(this.nameText);

    // 좌표는 보조 정보라 오른쪽 끝에 흐린 색으로 붙인다.
    this.locText = this.scene.add.text(width / 2 - 8, 0, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#8899bb',
    });
    this.locText.setOrigin(1, 0.5);
    this.add(this.locText);

    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    const hitArea = this.scene.add.rectangle(0, 0, width, height, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    hitArea.setScrollFactor(0);
    hitArea.on('pointerover', () => {
      if (!this.bookmark) return;
      this.bg.setFillStyle(ROW_BG_HOVER_COLOR);
      this.bg.setStrokeStyle(1, ROW_BORDER_HOVER_COLOR);
    });
    hitArea.on('pointerout', () => this.resetStyle());
    hitArea.on('pointerdown', () => {
      if (!this.bookmark) return;
      this.onPick(this.bookmark);
    });
    this.add(hitArea);
  }

  public setBookmark(bookmark: BookmarkEntry): void {
    this.bookmark = bookmark;
    this.nameText.setText(bookmark.name || `#${bookmark.id}`);
    this.locText.setText(`맵 ${bookmark.mapId} (${bookmark.x}, ${bookmark.y})`);
    this.bg.setVisible(true);
    this.resetStyle();
  }

  /** 빈 줄로 되돌린다. 배경까지 숨겨야 목록이 짧을 때 빈 칸이 눈에 남지 않는다. */
  public clear(): void {
    this.bookmark = null;
    this.nameText.setText('');
    this.locText.setText('');
    this.bg.setVisible(false);
    this.resetStyle();
  }

  private resetStyle(): void {
    this.bg.setFillStyle(ROW_BG_COLOR);
    this.bg.setStrokeStyle(1, ROW_BORDER_COLOR);
  }
}
